import { useEffect, useState, type FC } from 'react';
import { Button } from '@ui/button';
import { Skeleton } from '@ui/skeleton';
import { Check, LoaderCircle, Undo2 } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { API_DOCS, INSURANCE_DOCUMENT_TYPES } from '../consts/apiDocs.const';
import type { OrderSummaryProps } from '../models/types';
import { formatCheckoutPrice } from '../utils/formatCheckoutPrice';

/**
 * Компонент с информацией о заказе и итоговой суммой
 */
export const OrderSummary: FC<OrderSummaryProps> = ({
  totalPrice,
  serviceFee,
  discountAmount,
  totalAmount,
  onProceedToPayment,
  isSubmitting,
  validDateTime,
  insurancePremium,
  isInsuranceSelected,
  isCalculating,
}) => {
  const [isAgreed, setIsAgreed] = useState(false);
  const [isInsuranceAgreed, setIsInsuranceAgreed] = useState(false);
  const handleCheckboxToggle = () => {
    setIsAgreed(prev => !prev);
  };

  useEffect(() => {
    if (!isInsuranceSelected) {
      setIsInsuranceAgreed(false);
    }
  }, [isInsuranceSelected]);

  const renderCheckbox = (id: string, checked: boolean, onChange: () => void) => (
    <span className="relative mt-0.5 flex size-[18px] shrink-0">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="peer size-[18px] cursor-pointer appearance-none rounded border-[1.5px] border-input bg-background checked:border-insurance-primary checked:bg-insurance-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-insurance-primary focus-visible:ring-offset-2"
      />
      <Check
        className="pointer-events-none absolute inset-0 m-auto size-3 text-primary-foreground opacity-0 peer-checked:opacity-100"
        strokeWidth={3}
      />
    </span>
  );

  return (
    <div className="mt-5 border-t border-border pt-5">
      <div className="space-y-2.5 text-sm leading-5 text-muted-foreground">
        {totalPrice !== null && (
          <div className="flex items-center justify-between">
            <span>Билеты:</span>
            <span className={cn('transition-opacity', isCalculating && 'opacity-60')}>
              {formatCheckoutPrice(totalPrice)} ₽
            </span>
          </div>
        )}
        {discountAmount !== null && discountAmount > 0 && (
          <div className="flex items-center justify-between text-status-success">
            <span>Скидка:</span>
            <span className={cn('transition-opacity', isCalculating && 'opacity-60')}>
              -{formatCheckoutPrice(discountAmount)} ₽
            </span>
          </div>
        )}
        {(serviceFee !== null || isCalculating) && (
          <div className="flex items-center justify-between">
            <span>Сервисный сбор:</span>
            {serviceFee !== null ? (
              <span className={cn('transition-opacity', isCalculating && 'opacity-60')}>
                {formatCheckoutPrice(serviceFee)} ₽
              </span>
            ) : (
              <Skeleton className="h-5 w-16" />
            )}
          </div>
        )}
        {isInsuranceSelected && (insurancePremium !== null || isCalculating) && (
          <div className="flex items-center justify-between text-insurance-primary">
            <span className="flex items-center gap-1.5">
              <Undo2 className="size-[13px]" aria-hidden="true" />
              Полный возврат:
            </span>
            {insurancePremium !== null ? (
              <span className={cn('transition-opacity', isCalculating && 'opacity-60')}>
                +{formatCheckoutPrice(insurancePremium)} ₽
              </span>
            ) : (
              <Skeleton className="h-5 w-16" />
            )}
          </div>
        )}
      </div>

      {(totalAmount !== null || isCalculating) && (
        <div className="mt-5 flex items-center justify-between border-t border-border pt-1 text-base font-semibold leading-6 text-foreground">
          <span className="flex items-center gap-2">
            К оплате:
            {isCalculating && (
              <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
            )}
          </span>
          {totalAmount !== null ? (
            <span className={cn('transition-opacity', isCalculating && 'opacity-60')}>
              {formatCheckoutPrice(totalAmount)} ₽
            </span>
          ) : (
            <Skeleton className="h-6 w-24" />
          )}
        </div>
      )}

      <label
        htmlFor="agreement-checkbox"
        className="mt-5 flex cursor-pointer items-start gap-2.5 pl-4 text-sm font-medium leading-[22px] text-muted-foreground"
      >
        {renderCheckbox('agreement-checkbox', isAgreed, handleCheckboxToggle)}
        <span>Я даю согласие на обработку персональных данных</span>
      </label>

      {isInsuranceSelected && (
        <label
          htmlFor="insurance-agreement-checkbox"
          className="mt-5 flex cursor-pointer items-start gap-2.5 rounded-[14px] bg-insurance-muted p-4 text-sm font-medium leading-[22px] text-foreground"
        >
          {renderCheckbox('insurance-agreement-checkbox', isInsuranceAgreed, () =>
            setIsInsuranceAgreed(prev => !prev)
          )}
          <span>
            Я ознакомлен(-а) с{' '}
            <a
              href={API_DOCS.INSURANCE(INSURANCE_DOCUMENT_TYPES.KEY_INFORMATION_DOCUMENT)}
              target="_blank"
              rel="noreferrer"
              onClick={event => event.stopPropagation()}
              className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
            >
              Ключевым информационным документом
            </a>
            ,{' '}
            <a
              href={API_DOCS.INSURANCE(INSURANCE_DOCUMENT_TYPES.FINANCIAL_RISK_INSURANCE_RULES)}
              target="_blank"
              rel="noreferrer"
              onClick={event => event.stopPropagation()}
              className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
            >
              Правилами страхования
            </a>
            ,{' '}
            <a
              href={API_DOCS.INSURANCE(INSURANCE_DOCUMENT_TYPES.POLICY_DRAFT)}
              target="_blank"
              rel="noreferrer"
              onClick={event => event.stopPropagation()}
              className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
            >
              Условиями страхования
            </a>{' '}
            и даю{' '}
            <a
              href={API_DOCS.INSURANCE(INSURANCE_DOCUMENT_TYPES.PERSONAL_DATA_CONSENT)}
              target="_blank"
              rel="noreferrer"
              onClick={event => event.stopPropagation()}
              className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
            >
              Согласие на обработку персональных данных
            </a>{' '}
            страховщику ООО «Дефанс Страхование» и его агенту. Подтверждаю, что мне 18+ лет и я
            являюсь гражданином РФ.
          </span>
        </label>
      )}

      <Button
        disabled={
          isSubmitting ||
          isCalculating ||
          !isAgreed ||
          (isInsuranceSelected && !isInsuranceAgreed) ||
          !!validDateTime
        }
        onClick={onProceedToPayment}
        className={cn(
          'mt-5 h-12 w-full cursor-pointer rounded-[14px] bg-insurance-primary text-sm font-semibold text-primary-foreground hover:bg-insurance-primary-hover',
          'disabled:bg-insurance-primary/30 disabled:text-primary-foreground/60 disabled:opacity-100 disabled:cursor-not-allowed'
        )}
      >
        {validDateTime ? 'Ожидание оплаты...' : 'Перейти к оплате'}
      </Button>
    </div>
  );
};
